import { useEffect, useRef, useState } from "react";
import { get, increment, onValue, ref, set, update } from "firebase/database";
import { rtdb } from "../firebase";
import { PUBLIC_GAMES } from "./room";

// The 🔥 Most popular games. Each player counts once per game, ever: the first
// time someone plays a game, their own page marks popular/seen/{game}/{uid} and
// adds 1 to popular/plays/{game}. The same player playing more rounds, more
// games of it or again later isn't counted again. Every 200 players counted
// (200, 400, 600…), the page that counts takes a snapshot of the counts
// (popular/snapshot), and the top 3 of that snapshot are the popular ones until
// the next 200. Before the first 200, it's the three below.
export const DEFAULT_TOP = ["doodle", "tycoon", "telephone"];
export const TOP_EVERY = 200;
export const TOP_COUNT = 3;

// The games that can be popular (Party Shuffle isn't a game itself).
export const RANKED = Object.keys(PUBLIC_GAMES).filter((id) => id !== "shuffle");

// The top games of a snapshot ({ at, plays }), most played first. Ties go to
// the games that were popular from the start, then to the usual order.
export function topGames(snapshot) {
  if (!snapshot?.at || !snapshot.plays) return DEFAULT_TOP;
  const plays = snapshot.plays;
  const rank = (id) => (DEFAULT_TOP.includes(id) ? DEFAULT_TOP.indexOf(id) : DEFAULT_TOP.length + RANKED.indexOf(id));
  return [...RANKED].sort((a, b) => (plays[b] || 0) - (plays[a] || 0) || rank(a) - rank(b)).slice(0, TOP_COUNT);
}

// Game ids in display order: Party Shuffle first, then the popular games,
// then the rest as usual. `ids` are the games to show.
export function gameOrder(ids, top = DEFAULT_TOP) {
  const first = ids.filter((id) => id === "shuffle");
  const popular = top.filter((id) => ids.includes(id));
  return [...first, ...popular, ...ids.filter((id) => id !== "shuffle" && !popular.includes(id))];
}

// Everything counted so far ({ plays, snapshot }), live. null while loading.
export function useStats() {
  const [stats, setStats] = useState(null);
  useEffect(
    () =>
      onValue(
        ref(rtdb, "popular"),
        (s) => setStats(s.val() || {}),
        () => setStats({})
      ),
    []
  );
  return stats;
}

// Players counted so far, over all games.
export const totalOf = (plays) => Object.values(plays || {}).reduce((a, n) => a + (Number(n) || 0), 0);

// Is it time for a new snapshot? (The total reached another multiple of 200.)
export const snapshotDue = (total, snapshotAt) => Math.floor((total || 0) / TOP_EVERY) > Math.floor((snapshotAt || 0) / TOP_EVERY);

// The popular games right now (the defaults until the snapshot loads).
export function usePopular() {
  const [top, setTop] = useState(DEFAULT_TOP);
  useEffect(
    () =>
      onValue(
        ref(rtdb, "popular/snapshot"),
        (s) => setTop(topGames(s.val())),
        () => setTop(DEFAULT_TOP)
      ),
    []
  );
  return top;
}

// Counts `uid` as a player of `game`, once ever (the marker is theirs alone to
// write, and only once), then takes a new snapshot if the total reached
// another 200. Returns whether they were counted.
export async function countPlayer({ game, uid }) {
  if (!RANKED.includes(game) || !uid) return false;
  if ((await get(ref(rtdb, `popular/seen/${game}/${uid}`))).val()) return false;
  // One write: the marker and +1, which the rules only accept together.
  await update(ref(rtdb), { [`popular/seen/${game}/${uid}`]: true, [`popular/plays/${game}`]: increment(1) });
  await snapshotIfDue();
  return true;
}

// Takes a new snapshot if the total reached another 200.
async function snapshotIfDue() {
  const plays = (await get(ref(rtdb, "popular/plays"))).val() || {};
  const at = (await get(ref(rtdb, "popular/snapshot/at"))).val();
  if (snapshotDue(totalOf(plays), at)) {
    // The rules only accept a snapshot that matches the real counts, so if
    // someone else counted in between, this fails and the next count retries.
    await set(ref(rtdb, "popular/snapshot"), { at: totalOf(plays), plays }).catch(() => {});
  }
}

// For the owner (moderation page): adds `by` players (or takes some off, with
// a negative number) to a game's count, never below 0.
export async function adjustPlays(game, by) {
  if (!RANKED.includes(game) || !Number.isInteger(by)) return null;
  const now = (await get(ref(rtdb, `popular/plays/${game}`))).val() || 0;
  const next = Math.max(0, now + by);
  await set(ref(rtdb, `popular/plays/${game}`), next);
  await snapshotIfDue();
  return next;
}

// Everyone's own page counts them when a game of theirs is running.
export function useCountSelf({ meta, state, joined, uid }) {
  const done = useRef(new Set());
  const game = meta?.game;
  const playing = joined && meta?.status === "playing" && !!state;
  useEffect(() => {
    if (!playing || !uid || done.current.has(game)) return;
    done.current.add(game);
    countPlayer({ game, uid }).catch(() => done.current.delete(game));
  }, [playing, game, uid]);
}
